import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Flame, MessageCircleQuestion, Moon, Sun } from 'lucide-react';
import { useDepartments } from '@/api/queries';
import type { Me } from '@/api/types';
import { CommandPalette, type CommandItem } from '@/components/CommandPalette';
import { NAV_ITEMS } from '@/app/nav';
import { useTheme } from '@/hooks/useTheme';

/** Navigation + department jumps. L7 can extend this with natural-language commands. */
export function AppCommandPalette({ me, onAsk }: { me?: Me; onAsk: () => void }) {
  const navigate = useNavigate();
  const departments = useDepartments(me);
  const { theme, toggleTheme } = useTheme();
  const roles = useMemo(() => new Set(me?.roles.map((r) => r.role) ?? []), [me]);

  const items = useMemo<CommandItem[]>(() => {
    const nav: CommandItem[] = NAV_ITEMS.filter((n) => !n.roles || n.roles.some((r) => roles.has(r))).map((n) => ({
      id: `nav:${n.to}`,
      label: n.label,
      hint: n.description,
      group: 'Go to',
      icon: <n.icon className="h-4 w-4" aria-hidden />,
      run: () => navigate(n.to),
    }));

    const depts: CommandItem[] = (departments.data ?? []).map((d) => ({
      id: `dept:${d.key}`,
      label: d.name,
      hint: d.canEdit ? 'You can edit this department' : 'Read-only for you',
      group: 'Departments',
      icon: <Building2 className="h-4 w-4" aria-hidden />,
      run: () => navigate(`/registry?dept=${encodeURIComponent(d.key)}`),
    }));

    const actions: CommandItem[] = [
      {
        id: 'action:ask',
        label: 'Ask CIGS',
        hint: 'Answer a question from the registry and the library, with citations',
        group: 'Actions',
        icon: <MessageCircleQuestion className="h-4 w-4" aria-hidden />,
        run: onAsk,
      },
      {
        id: 'action:hot-list',
        label: 'Show hot-list capabilities',
        hint: 'Registry filtered to the hot list',
        group: 'Actions',
        icon: <Flame className="h-4 w-4" aria-hidden />,
        run: () => navigate('/registry?hot=1'),
      },
      {
        id: 'action:theme',
        label: theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme',
        group: 'Actions',
        icon: theme === 'dark' ? <Sun className="h-4 w-4" aria-hidden /> : <Moon className="h-4 w-4" aria-hidden />,
        run: toggleTheme,
      },
    ];

    return [...nav, ...depts, ...actions];
  }, [departments.data, navigate, onAsk, roles, theme, toggleTheme]);

  return <CommandPalette items={items} />;
}
